import { useCallback, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { generatePath } from 'react-router';
import { useSelector } from 'react-redux';
import { getUserToken } from '@/redux/user';
import { usePostWebhookMutation } from '@/services/innovorder/moduleExernalWebhooks/moduleExternalWebhooks.endpoints';
import { Routes } from '@/core/router/routes.types';
import {
    WebhookConfig,
    FormRefType,
    DEFAULT_WEBHOOK_CHANNELS,
    buildEventTypeString,
} from '@/pages/Config/Webhooks/types';
import { CreateBrandWebhookViewModel } from './types';

export const useCreateBrandWebhookVM = (): CreateBrandWebhookViewModel => {
    const [isValid, setValid] = useState(false);
    const formRef = useRef<FormRefType>();
    const token = useSelector(getUserToken);
    const { brandId } = useParams<{ brandId: string }>();
    const [postWebhook, { isLoading: isPostLoading, error: postError }] = usePostWebhookMutation();
    const history = useHistory();

    const onSubmit = useCallback(
        async (data: WebhookConfig) => {
            const eventTypeString = buildEventTypeString(data.eventTypes);

            const webhookId = await postWebhook({
                token,
                body: {
                    brandId: Number(brandId),
                    webhookUrl: data.webhookUrl,
                    channels: data.channels,
                    eventType: eventTypeString,
                    additionalHeaders: JSON.stringify(data.additionalHeaders),
                },
            }).unwrap();

            if (webhookId) {
                history.push(generatePath(Routes.EditBrandWebhook, { brandId, webhookId }));
            }
        },
        [brandId, history, postWebhook, token],
    );

    const webhookConfigDefaultData: WebhookConfig = {
        webhookUrl: '',
        webhookHmacSecret: '',
        channels: DEFAULT_WEBHOOK_CHANNELS,
        additionalHeaders: [],
    };

    const onSave = useCallback(() => {
        formRef.current?.handleSubmit(onSubmit)();
    }, [onSubmit]);

    return {
        isValid,
        setValid,
        formRef,
        isPostLoading,
        postError,
        onSubmit,
        webhookConfigDefaultData,
        onSave,
    };
};
